import { useState } from 'react';
import { LazyImage } from '@/components/ui/LazyImage';
import type { SectionProps } from '@/types';

export function GallerySection({ business, config }: SectionProps) {
  const title = (config.title as string) || 'Gallery';
  const layout = (config.layout as string) || 'grid';
  const gallery = business.gallery;

  if (!gallery.length) return null;

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <section className="section-card">
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>

      <div
        className={
          layout === 'masonry'
            ? 'mt-6 columns-2 gap-3 sm:columns-3'
            : 'mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4'
        }
      >
        {gallery.map((item, index) => (
          <button
            key={item.url}
            type="button"
            className={`overflow-hidden rounded-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary ${
              layout === 'masonry' ? 'mb-3 break-inside-avoid' : ''
            }`}
            onClick={() => setLightboxIndex(index)}
            aria-label={`View image ${index + 1}`}
          >
            <LazyImage
              src={item.url}
              alt={item.alt_text ?? `${business.name} gallery ${index + 1}`}
              thumbnailSrc={item.thumbnail_url}
              aspectRatio={layout === 'masonry' ? 'aspect-auto' : 'aspect-square'}
              className="w-full transition hover:scale-105"
            />
          </button>
        ))}
      </div>

      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          role="dialog"
          aria-modal
          aria-label="Image gallery"
          onClick={() => setLightboxIndex(null)}
          onKeyDown={(e) => e.key === 'Escape' && setLightboxIndex(null)}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            onClick={() => setLightboxIndex(null)}
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={gallery[lightboxIndex].url}
            alt={gallery[lightboxIndex].alt_text ?? business.name}
            className="max-h-[90vh] max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
